import type { AnalyticsEvents } from 'src/modules/analytics/analytics';
import {
  classifyRestoredResult,
  extractPhotosFromRestoredResult,
  type RestoredPhoto,
  type RestoredPhotoScope,
} from 'src/modules/camera-session';

export const RESTORED_WAIT_MS = 1000;

export type RestoredCameraResult = {
  pluginId: string;
  methodName: string;
  success: boolean;
  data?: unknown;
  error?: { message?: string };
};

export type CameraRestoreDeps = {
  isNative: () => boolean;
  getPlatform: () => string;
  now: () => number;
  schedule: (fn: () => void, ms: number) => unknown;
  logAnalyticsEvent: (event: AnalyticsEvents, params?: Record<string, unknown>) => void;
  reportError: (message: string, context?: unknown) => void;
  setRestoredCameraPhotos: (photos: RestoredPhoto[], scope: RestoredPhotoScope) => void;
};

export const createCameraRestoreTracker = (deps: CameraRestoreDeps) => {
  // Destructured bare on purpose: the inventory extractor only finds bare call sites. Deps must also
  // be called unbound: a WebView global like setTimeout throws "Illegal invocation" with `this` = deps.
  const {
    isNative,
    getPlatform,
    now,
    schedule,
    logAnalyticsEvent,
    reportError,
    setRestoredCameraPhotos,
  } = deps;
  let result: RestoredCameraResult | null = null;
  let resolveWait: (() => void) | null = null;
  // Set when the fast-track gives up waiting; a result after that is "late".
  let waitTimedOutAt: number | null = null;
  let lateScope: RestoredPhotoScope | null = null;

  const onRestoredResult = (data: RestoredCameraResult) => {
    if (data.pluginId !== 'Camera') return;
    result = data;
    resolveWait?.();
    if (waitTimedOutAt === null) return;
    const lateMs = Math.round(now() - waitTimedOutAt);
    logAnalyticsEvent('camera_result_late', {
      lateMs,
      methodName: data.methodName,
      outcome: classifyRestoredResult(data, data.methodName),
      platform: getPlatform(),
    });
    if (data.success && data.data && lateScope) {
      const photos = extractPhotosFromRestoredResult(data.data, data.methodName);
      if (photos.length) setRestoredCameraPhotos(photos, lateScope);
    }
  };

  const setLateScope = (scope: RestoredPhotoScope) => {
    lateScope = scope;
  };

  const waitForResult = (): Promise<void> => {
    // appRestoredResult is Android-only; iOS never emits it.
    if (!isNative() || getPlatform() !== 'android' || result) return Promise.resolve();
    return new Promise<void>((resolve) => {
      resolveWait = resolve;
      schedule(() => {
        if (!result) waitTimedOutAt = now();
        resolve();
      }, RESTORED_WAIT_MS);
    });
  };

  const hasResult = () => result !== null;

  // A null scope reports without injecting photos (no orphaned session, no grid to feed).
  const report = (scope: RestoredPhotoScope | null) => {
    const outcome = classifyRestoredResult(result, result?.methodName ?? '');
    logAnalyticsEvent('camera_result_restored', {
      outcome,
      success: result?.success ?? false,
      ...(result && { methodName: result.methodName }),
      platform: getPlatform(),
    });
    if (!result) return;
    reportError('Camera activity restored after OS kill (appRestoredResult)', {
      pluginId: result.pluginId,
      methodName: result.methodName,
      success: result.success,
      hasData: !!result.data,
      outcome,
      platform: getPlatform(),
    });
    logAnalyticsEvent('camera_activity_restored', {
      methodName: result.methodName,
      success: result.success,
      hasData: !!result.data,
      platform: getPlatform(),
    });
    if (outcome === 'photo' && scope) {
      setRestoredCameraPhotos(
        extractPhotosFromRestoredResult(result.data, result.methodName),
        scope,
      );
    }
  };

  return { onRestoredResult, setLateScope, waitForResult, hasResult, report };
};
